import { FC, useState } from 'react';
import { useHistory } from 'react-router';
import { Flex, HStack, SimpleGrid } from '@chakra-ui/react';
import { ERROR_CODES } from '@shinetools/errors';
import { ColorName as SunshineColorName } from '@shinetools/sunshine';

import {
  FeatureFilterField,
  FeatureName,
  Role,
  TeamMemberRole,
  TeamMemberStatus,
} from '__generated__/GQL';
import { getServerErrorCode } from 'common/errors/utils';
import useCanCompanyUserAccessFeature from 'common/hooks/useCanCompanyUserAccessFeature';
import useCompanyContext from 'common/hooks/useCompanyContext';
import SunshineIconButton from 'components/_core/IconButton';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineDropdown, { ItemProps } from 'components/_core/SunshineDropdown';
import SunshineIcon from 'components/_core/SunshineIcon';
import SunshineTag from 'components/_core/SunshineTag';
import Text from 'components/_core/Text';
import useToast from 'components/ToastProvider/useToast';
import * as bankRoutes from 'features/Bank/routes';
import { TeamMember } from 'features/Team/hooks/useTeamMembers';
import { useUnlockUser } from 'features/Team/pages/Members/hooks/useUnlockUser';
import { getHighestRole, hasRole, isFreeMember } from 'features/Team/utils';

import InvitationDeletionModal from '../InvitationDeletionModal';
import LockTeamMemberModal from '../LockTeamMemberModal';
import UnlockTeamMemberModal from '../UnlockTeamMemberModal';
import { getMemberRoleTransitionPath } from './../../../../../../routes';
import { useResendInvitation } from './hooks/useResendInvitation';
import locales from './locales';
import { MenuButtonsIds } from './types';

interface TeamMemberListItemProps {
  canUnlockMember: boolean;
  hasInvitationsLeft: boolean;
  onInvitationQuotaError: () => void;
  onUnlockUser: () => void;
  isFreeRolesOnly: boolean;
  teamMember: TeamMember;
}

enum ModalIds {
  LOCK_TEAM_MEMBER,
  INVITATION_DELETION,
  UNLOCK_TEAM_MEMBER,
}

const TeamMemberListItem: FC<TeamMemberListItemProps> = ({
  canUnlockMember,
  hasInvitationsLeft,
  isFreeRolesOnly,
  onInvitationQuotaError,
  onUnlockUser,
  teamMember,
}) => {
  const {
    companyUserOrInvitationId,
    email,
    firstName,
    lastName,
    paymentCardsCount,
    roles,
    status,
  } = teamMember;

  const { companyUserId: currentCompanyUserId } = useCompanyContext();
  const { canAccessFeature } = useCanCompanyUserAccessFeature();
  const { showToast } = useToast();
  const [modalId, setModalId] = useState<ModalIds>();
  const history = useHistory();

  const [resendInvitation, { loading: resendInvitationLoading }] =
    useResendInvitation(companyUserOrInvitationId);

  const [unlockCompanyUser, { loading: unlockCompanyUserLoading }] =
    useUnlockUser(companyUserOrInvitationId);

  const numberOfCardsColor = () =>
    hasRole(teamMember, TeamMemberRole.Accountant) ? 'grey.500' : 'grey.800';

  const canRevokeAdmin = canAccessFeature({
    name: FeatureName.TeamMembersRevoke,
    where: {
      or: [
        {
          field: FeatureFilterField.Role,
          values: [Role.Admin],
        },
      ],
    },
  });

  const canInviteAdmin = canAccessFeature({
    name: FeatureName.TeamMembersRevoke,
    where: {
      or: [
        {
          field: FeatureFilterField.Role,
          values: [Role.Admin],
        },
      ],
    },
  });

  const doUnlock = async () => {
    try {
      await unlockCompanyUser();
      showToast({
        message: locales.formatString(locales.unlockConfirmation, {
          firstName,
          lastName,
        }) as string,
        type: 'success',
      });
      onUnlockUser();
    } catch (error) {
      const graphQlErrorCode = getServerErrorCode(error);

      if (
        graphQlErrorCode === ERROR_CODES.COMPANY_INVITATION_QUOTA_LIMIT_REACHED
      ) {
        onInvitationQuotaError();
        return;
      }

      showToast({
        message: locales.errors.invitationError,
        type: 'error',
      });
    }
  };

  const handleUnlockUserClick = async () => {
    if (hasInvitationsLeft || isFreeMember(teamMember)) {
      doUnlock();
    } else {
      setModalId(ModalIds.UNLOCK_TEAM_MEMBER);
    }
  };

  const handleResendInvitationClick = async () => {
    try {
      await resendInvitation();
      showToast({
        message: locales.formatString(locales.resendInvitationConfirmation, {
          firstName,
          lastName,
        }) as string,
        type: 'success',
      });
    } catch (error) {
      showToast({
        message: locales.errors.invitationError,
        type: 'error',
      });
    }
  };

  const menuButtons: Record<MenuButtonsIds, ItemProps> = {
    [MenuButtonsIds.ACCESS_TRANSACTIONS]: {
      children: locales.menu.accessTransactions,
      icon: 'eye-open',
      id: MenuButtonsIds.ACCESS_TRANSACTIONS,
      onClick: () =>
        history.push(
          bankRoutes.getTransactionsByCompanyUserIdPath(
            companyUserOrInvitationId,
          ),
        ),
    },
    [MenuButtonsIds.TRANSITION_TO_ROLE]: {
      children: locales.menu.transitionToRole,
      icon: 'personal',
      id: MenuButtonsIds.TRANSITION_TO_ROLE,
      onClick: () =>
        history.push(getMemberRoleTransitionPath(companyUserOrInvitationId)),
    },
    [MenuButtonsIds.ACCESS_OWN_TRANSACTIONS]: {
      children: locales.menu.accessOwnTransactions,
      icon: 'eye-open',
      id: MenuButtonsIds.ACCESS_OWN_TRANSACTIONS,
      onClick: () =>
        history.push(
          bankRoutes.getTransactionsByCompanyUserIdPath(
            companyUserOrInvitationId,
          ),
        ),
    },
    [MenuButtonsIds.LOCK_MEMBER]: {
      children: locales.menu.lockMember,
      colorScheme: 'red',
      icon: 'cross',
      id: MenuButtonsIds.LOCK_MEMBER,
      onClick: () => setModalId(ModalIds.LOCK_TEAM_MEMBER),
    },
    [MenuButtonsIds.DELETE_INVITATION]: {
      children: locales.menu.deleteInvitation,
      colorScheme: 'red',
      icon: 'cross',
      id: MenuButtonsIds.DELETE_INVITATION,
      onClick: () => setModalId(ModalIds.INVITATION_DELETION),
    },
    [MenuButtonsIds.SEND_AGAIN_INVITATION]: {
      children: locales.menu.sendAgainInvitation,
      icon: 'send',
      id: MenuButtonsIds.SEND_AGAIN_INVITATION,
      onClick: handleResendInvitationClick,
    },
    [MenuButtonsIds.UNLOCK_MEMBER]: {
      children: locales.menu.unlockMember,
      icon: 'send',
      id: MenuButtonsIds.UNLOCK_MEMBER,
      isDisabled: !canUnlockMember,
      onClick: handleUnlockUserClick,
    },
  };

  const invitationSentButtons = [
    menuButtons.sendAgainInvitation,
    menuButtons.deleteInvitation,
  ];

  const validationOngoingButtons = [menuButtons[MenuButtonsIds.LOCK_MEMBER]];

  const roleTransitionMenuItems =
    !teamMember.hasOngoingInvitation && !isFreeRolesOnly
      ? [menuButtons[MenuButtonsIds.TRANSITION_TO_ROLE]]
      : [];

  const employeeButtons =
    status === TeamMemberStatus.Locked
      ? [menuButtons.unlockMember]
      : [
          menuButtons.accessTransactions,
          ...roleTransitionMenuItems,
          menuButtons[MenuButtonsIds.LOCK_MEMBER],
        ];

  const getAdminButtons = (): ItemProps[] => {
    if (status === TeamMemberStatus.Locked) {
      return canInviteAdmin ? [menuButtons.unlockMember] : [];
    }

    return [
      ...(currentCompanyUserId === companyUserOrInvitationId
        ? [menuButtons[MenuButtonsIds.ACCESS_OWN_TRANSACTIONS]]
        : [
            menuButtons[MenuButtonsIds.ACCESS_TRANSACTIONS],
            ...roleTransitionMenuItems,
          ]),
      ...(canRevokeAdmin ? [menuButtons[MenuButtonsIds.LOCK_MEMBER]] : []),
    ];
  };

  const accountantButtons =
    status === TeamMemberStatus.Locked
      ? [menuButtons.unlockMember]
      : [...roleTransitionMenuItems, menuButtons[MenuButtonsIds.LOCK_MEMBER]];

  const accountHolderButtons =
    currentCompanyUserId === companyUserOrInvitationId
      ? [menuButtons.accessOwnTransactions]
      : [menuButtons.accessTransactions];

  const dropdownByRole = {
    [TeamMemberRole.None]: [],
    [TeamMemberRole.Accountant]: accountantButtons,
    [TeamMemberRole.AccountHolder]: accountHolderButtons,
    [TeamMemberRole.Employee]: employeeButtons,
    [TeamMemberRole.Admin]: getAdminButtons(),
  };

  const getBadgeColorStatus = (): SunshineColorName => {
    switch (status) {
      case TeamMemberStatus.Active:
        return 'green';
      case TeamMemberStatus.Locked:
        return 'red';
      default:
        return 'blue';
    }
  };

  const getDropdownButtons = () => {
    switch (status) {
      case TeamMemberStatus.InvitationSent:
        return invitationSentButtons;
      case TeamMemberStatus.ValidationOngoing:
        return validationOngoingButtons;
      default:
        return dropdownByRole[getHighestRole(roles ?? []) as TeamMemberRole];
    }
  };

  const menuOptions: ItemProps[] = getDropdownButtons();

  const invitationRole =
    teamMember.hasOngoingInvitation &&
    teamMember.ongoingInvitationRole &&
    !roles?.includes(teamMember.ongoingInvitationRole) &&
    teamMember.isCompanyUser
      ? teamMember.ongoingInvitationRole
      : null;

  return (
    <SunshineCard>
      <SimpleGrid
        alignItems="center"
        columns={3}
        spacing="space-24"
        width="100%"
      >
        <SunshineCard.Content>
          <SunshineCard.Title noOfLines={1} wordBreak="break-all">
            {firstName} {lastName}
          </SunshineCard.Title>

          <SunshineCard.Text noOfLines={1} wordBreak="break-all">
            {email}
          </SunshineCard.Text>
        </SunshineCard.Content>

        <Flex alignItems="center" gap=".5em" justifyContent="flex-start">
          {roles?.length ? (
            <Text fontWeight="weight-500">
              {roles?.map((r) => locales.role[r]).join(', ') ?? ''}
            </Text>
          ) : null}
          {invitationRole ? (
            <>
              <SunshineIcon name="arrow-right" />
              <Text fontWeight="weight-500">
                {locales.role[invitationRole]}
              </Text>
            </>
          ) : null}
        </Flex>

        <Flex alignItems="center" justifyContent="flex-end">
          <SunshineTag
            colorScheme={getBadgeColorStatus()}
            flexShrink={0}
            marginRight="space-64"
            size="sm"
          >
            {locales.status[status]}
          </SunshineTag>
          <HStack marginRight="space-24" spacing="space-6">
            <Text color={numberOfCardsColor()}>{paymentCardsCount}</Text>

            <SunshineIcon
              color={numberOfCardsColor()}
              name="card-recto"
              size="icon-22"
            />
          </HStack>
          <SunshineDropdown>
            <SunshineDropdown.Trigger>
              <SunshineIconButton
                aria-label="Actions"
                icon="vertical-ellipsis"
                isDisabled={
                  !roles || roles.length === 0 || menuOptions.length === 0
                }
                isLoading={resendInvitationLoading || unlockCompanyUserLoading}
                variant="secondary"
              />
            </SunshineDropdown.Trigger>

            <SunshineDropdown.List>
              {menuOptions.map(({ id, ...props }) => (
                <SunshineDropdown.Item key={id} {...props} />
              ))}
            </SunshineDropdown.List>
          </SunshineDropdown>
          <LockTeamMemberModal
            isOpen={
              modalId === ModalIds.LOCK_TEAM_MEMBER &&
              (status === TeamMemberStatus.Active ||
                status === TeamMemberStatus.ValidationOngoing)
            }
            onClose={() => setModalId(undefined)}
            user={{
              companyUserOrInvitationId,
              firstName,
              lastName,
              roles,
            }}
          />
          <InvitationDeletionModal
            isOpen={
              (status === TeamMemberStatus.InvitationSent ||
                !hasRole(teamMember, TeamMemberRole.AccountHolder)) &&
              modalId === ModalIds.INVITATION_DELETION
            }
            onClose={() => setModalId(undefined)}
            user={{
              companyUserOrInvitationId,
              firstName,
              lastName,
            }}
          />
          <UnlockTeamMemberModal
            isOpen={
              status === TeamMemberStatus.Locked &&
              modalId === ModalIds.UNLOCK_TEAM_MEMBER
            }
            onClose={() => setModalId(undefined)}
            onInvitationQuotaError={onInvitationQuotaError}
            onUnlockUser={onUnlockUser}
            user={{
              companyUserOrInvitationId,
              firstName,
              lastName,
            }}
          />
        </Flex>
      </SimpleGrid>
    </SunshineCard>
  );
};

export default TeamMemberListItem;
